"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, Rocket, Sparkles } from "lucide-react";
import { THome_Config } from "@/app/config";
import Link from "next/link";
import { motion } from "motion/react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.2,
    },
  },
};

export const HeroSection = ({ config }: { config: THome_Config["hero"] }) => {
  const featureCards = [
    {
      title: "Zuma-AI Education",
      description:
        "Smart learning platform with board-aligned content and AI tutoring",
      image: "/munkee.gif",
      iconClassName: "text-[#155dfc] bg-[#155dfc]/10", // blue
      bgColor: "bg-gray-100",
    },
    {
      title: "Zuma-AI Industries",
      description:
        "Enterprise AI solutions for business transformation and optimization",
      image: "/foxy_home.gif",
      iconClassName: "text-[#9810fa] bg-[#9810fa]/10", // violet
      bgColor: "bg-gray-100",
    },
    {
      title: "Zuma-Jobs Candidates",
      description:
        "AI-powered career matching and professional development platform",
      image: "/elee.gif",
      iconClassName: "text-[#00a63e] bg-[#00a63e]/10", // green
      bgColor: "bg-gray-100",
    },
    {
      title: "Zuma-Jobs Companies",
      description:
        "Intelligent recruitment platform for finding and hiring top talent",
      image: "/tigee2.gif",
      iconClassName: "text-[#f44900] bg-[#f44900]/10", // red
      bgColor: "bg-gray-100",
    },
  ];
  return (
    <section className="relative w-full bg-[linear-gradient(160deg,rgba(249,250,251,1)_0%,rgba(255,255,255,1)_50%,rgba(249,250,251,1)_100%)] py-20 overflow-hidden">
      {/* Animated background blobs */}
      <motion.div
        className="absolute w-[252px] h-[252px] top-[70px] left-[35px] bg-[#FFD6A7]/40 rounded-full blur-[32px]"
        variants={{
          animate: {
            y: [-10, 10, -10],
            x: [-5, 5, -5],
            transition: {
              duration: 6,
              ease: "easeInOut",
              repeat: Infinity,
            },
          },
        }}
        animate="animate"
      />
      <motion.div
        className="absolute w-[336px] h-[336px] top-[746px] right-[35px] bg-[#FFD6A7]/40 rounded-full blur-[32px]"
        variants={{
          animate: {
            y: [-10, 10, -10],
            x: [-5, 5, -5],
            transition: {
              duration: 6,
              ease: "easeInOut",
              repeat: Infinity,
            },
          },
        }}
        animate="animate"
        transition={{ delay: 2 }}
      />

      {/* Left side background images - First 2 cards */}
      <div className=" absolute left-0 top-1/4 -translate-y-1/2 w-full h-full pointer-events-none hidden xl:block">
        {featureCards.slice(0, 2).map((card, index) => (
          <motion.div
            key={`left-${index}`}
            className="absolute"
            style={{
              top: `${30 + index * 50}%`,
              left: `0`,
            }}
            initial={{ opacity: 0, x: -100, rotate: -15 }}
            animate={{
              opacity: 0.9,
              x: 0,
              rotate: -15,
            }}
            transition={{
              duration: 1.5,
              delay: index * 0.3,
              ease: "easeOut",
            }}
          >
            <motion.div
              animate={{
                y: [0, -30, 0],
                rotate: [-15, -12, -15],
              }}
              transition={{
                duration: 8,
                delay: index * 1,
                ease: "easeInOut",
                repeat: Infinity,
              }}
              className="relative"
            >
              <div
                className={`w-[252px] h-[252px] ${card.bgColor} rounded-[40px] flex items-center justify-center shadow-2xl border border-gray-200 backdrop-blur-sm`}
              >
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Right side background images - Last 2 cards */}
      <div className="absolute right-0 top-1/3 w-full h-full pointer-events-none hidden xl:block">
        {featureCards.slice(2, 4).map((card, index) => (
          <motion.div
            key={`right-${index}`}
            className="absolute"
            style={{
              top: `${-25 + index * 50}%`,
              right: `0`,
            }}
            initial={{ opacity: 0, x: 100, rotate: 15 }}
            animate={{
              opacity: 0.9,
              x: 0,
              rotate: 15,
            }}
            transition={{
              duration: 1.5,
              delay: index * 0.3 + 0.5,
              ease: "easeOut",
            }}
          >
            <motion.div
              animate={{
                y: [0, 30, 0],
                rotate: [15, 18, 15],
              }}
              transition={{
                duration: 8,
                delay: index * 1 + 2,
                ease: "easeInOut",
                repeat: Infinity,
              }}
              className="relative"
            >
              <div
                className={`w-[252px] h-[252px] ${card.bgColor} rounded-[40px] flex items-center justify-center shadow-2xl border border-gray-200 backdrop-blur-sm `}
              >
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="container mx-auto max-w-[1120px] px-6 flex flex-col items-center gap-[70px] relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.header
          className="flex flex-col items-center gap-7 text-center max-w-[784px]"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: "easeOut",
              },
            },
          }}
        >
          <motion.div
            className="inline-flex items-center gap-2 py-2 px-4 bg-white rounded-full border backdrop-blur-sm backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] text-[#354152] text-[12.3px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica]"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, ease: "linear", repeat: Infinity }}
            >
              <Sparkles className="w-5 h-5 text-[#FF6900]" />
            </motion.div>
            {config.tag}
          </motion.div>

          <div className="space-y-7">
            <motion.div
              className="flex flex-col items-center gap-7 text-center"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
            >
              <motion.h1
                className="text-[#495565] text-6xl [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {config.title}
                <motion.span
                  className="text-[#ffbf00]"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  {config.highlighted_title}
                </motion.span>
                <br />
                {config.sub_title}
              </motion.h1>
            </motion.div>

            <motion.h1
              className="text-[#495565] text-[17.5px] leading-[28.4px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
            >
              {config.description}
            </motion.h1>

            <motion.div
              className="flex items-center justify-center gap-3.5"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
            >
              <Link
                href={config.button.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Button className="h-auto bg-[#ffbf00] hover:bg-[#ffbf00]/90 rounded-[12.75px] px-4 py-2 text-white text-[15.8px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal">
                    <motion.div
                      animate={{ x: [0, 3, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Rocket className="w-4 h-4" />
                    </motion.div>
                    {config.button.title}
                  </Button>
                </motion.div>
              </Link>
              <Link href="/app-download">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Button
                    variant="outline"
                    className="h-auto bg-white hover:bg-amber-50 rounded-[12.75px] px-4 py-2 border-[#ffbf00]/40 text-[#f44900] text-[15.7px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal"
                  >
                    <Download className="w-4 h-4" />
                    Get Zuma AI App
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </motion.header>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full"
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.1,
                delayChildren: 0.6,
              },
            },
          }}
        >
          {config.featureCards.map((card, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.9 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.7,
                    ease: "easeOut",
                  },
                },
                hover: {
                  y: -8,
                  scale: 1.02,
                  transition: {
                    duration: 0.3,
                    ease: "easeInOut",
                  },
                },
              }}
              whileHover="hover"
            >
              <Card className="bg-white hover:bg-yellow-50 rounded-[12.75px] border backdrop-blur-sm backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] p-1 h-full">
                <CardContent className="flex flex-col items-center text-center p-7 h-[228.82px]">
                  <motion.div
                    className={`flex w-16 h-16 items-center justify-center ${card.bgColor} rounded-[12.75px] mb-6`}
                    whileHover={{
                      rotateY: 360,
                      transition: { duration: 0.6, ease: "easeInOut" },
                    }}
                  >
                    <img src={card.image} alt={card.title} />
                  </motion.div>

                  <motion.h3
                    className="text-frail-scale-05242098figmasiteebony text-[17.5px] leading-[24.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {card.title}
                  </motion.h3>

                  <motion.p
                    className="text-[#495565] text-[11.9px] leading-[19.9px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {card.description}
                  </motion.p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-7 max-w-[784px] w-full"
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.1,
                delayChildren: 1,
              },
            },
          }}
        >
          {config.statistics.map((stat, index) => (
            <motion.div
              key={index}
              className="flex flex-col items-center gap-2 text-center"
              variants={{
                hidden: { opacity: 0, scale: 0.5 },
                visible: {
                  opacity: 1,
                  scale: 1,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
              whileHover={{
                scale: 1.1,
                transition: { duration: 0.2 },
              }}
            >
              <motion.div
                className={`text-[30px] leading-[35px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal ${stat.gradient}`}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.1 + 1.2,
                  type: "spring",
                  stiffness: 200,
                }}
              >
                {stat.value}
              </motion.div>
              <motion.div
                className="text-[#495565] text-sm leading-[21px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 1.4 }}
              >
                {stat.label}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
